import { useEffect, useMemo, useState, useRef } from "react";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  startAfter,
} from "firebase/firestore";
import {
  CheckCircle2,
  Copy,
  Download,
  Loader2,
  Pencil,
  Percent,
  Plus,
  Search,
  Tag,
  TicketPercent,
  ToggleLeft,
  ToggleRight,
  Trash2,
  Users,
  X,
  IndianRupee,
} from "lucide-react";
import { db } from "../../firebase";
import AdminLayout from "../layout/AdminLayout";
import ConfirmDialog from "../../Components/ConfirmDialog";
import {
  downloadCsv,
  formatCurrency,
  formatDiscountLabel,
  normalizeCouponInput,
  toCsv,
} from "../../utils/coupons";
import { formatMembershipDate, parseMembershipDate } from "../../utils/membership";

const PLAN_OPTIONS = [
  { id: "premium_6months", name: "6 Months Premium (₹4,999)" },
  { id: "premium_annual", name: "1 Year Premium (₹6,999)" },
];

const emptyForm = {
  code: "",
  title: "",
  description: "",
  discountType: "percent",
  discountValue: "",      // percent: 0-100 | flat: rupees
  maxDiscountCap: "",     // rupees
  usageLimit: "",
  perUserUsageLimit: "1",
  minimumAmount: "",      // rupees
  badge: "Limited",
  visible: true,
  active: true,
  allowedPlans: [],
  expiresAt: "",
};

function toDateTimeLocal(value) {
  const date = parseMembershipDate(value);
  if (!date) return "";
  const pad = (item) => String(item).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

const rupeesToPaise = (rupees) => Math.round((Number(rupees) || 0) * 100);
const paiseToRupees = (paise) => (Number(paise) || 0) / 100;

export default function Coupons() {
  const [coupons, setCoupons] = useState([]);
  const [plans, setPlans] = useState(PLAN_OPTIONS);
  const [usage, setUsage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState("");
  const [notice, setNotice] = useState({ tone: "", message: "" });
  const [copiedCode, setCopiedCode] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const sentinelRef = useRef(null);
  const PAGE_SIZE = 100;
  const queryClient = useQueryClient();

  const couponsQuery = useInfiniteQuery({
    queryKey: ["adminCouponsPageData", "coupons"],
    queryFn: async ({ pageParam = null }) => {
      const q = pageParam
        ? query(collection(db, "coupons"), orderBy("createdAt", "desc"), startAfter(pageParam), limit(PAGE_SIZE))
        : query(collection(db, "coupons"), orderBy("createdAt", "desc"), limit(PAGE_SIZE));
      const snap = await getDocs(q);
      return { coupons: snap.docs.map((d) => ({ id: d.id, ...d.data() })), nextCursor: snap.docs.length === PAGE_SIZE ? snap.docs[snap.docs.length - 1] : null };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 300000,
    cacheTime: 1800000,
    retry: 1,
  });

  const plansQuery = useQuery({
    queryKey: ["adminCouponsPageData", "plans"],
    queryFn: async () => {
      const snap = await getDocs(query(collection(db, "membershipPlans"), orderBy("createdAt", "desc")));
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    },
    staleTime: 300000,
    cacheTime: 1800000,
    retry: 1,
  });

  const usageQuery = useQuery({
    queryKey: ["adminCouponsPageData", "usage"],
    queryFn: async () => {
      const snap = await getDocs(query(collection(db, "couponUsage"), orderBy("redeemedAt", "desc"), limit(200)));
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    },
    staleTime: 300000,
    cacheTime: 1800000,
    retry: 1,
  });

  useEffect(() => {
    if (couponsQuery.data) {
      setCoupons(couponsQuery.data.pages.flatMap((p) => p.coupons));
    }
    if (plansQuery.data && plansQuery.data.length) setPlans(plansQuery.data);
    if (usageQuery.data) setUsage(usageQuery.data);
    setLoading(couponsQuery.isLoading || plansQuery.isLoading || usageQuery.isLoading);
  }, [couponsQuery.data, plansQuery.data, usageQuery.data, couponsQuery.isLoading, plansQuery.isLoading, usageQuery.isLoading]);

  useEffect(() => {
    if (!sentinelRef.current || !couponsQuery.hasNextPage || couponsQuery.isLoading || couponsQuery.isFetchingNextPage) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (!entries[0]?.isIntersecting) return;
      couponsQuery.fetchNextPage().catch(() => {});
    }, { rootMargin: "300px 0px" });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [couponsQuery.hasNextPage, couponsQuery.isLoading, couponsQuery.isFetchingNextPage]);

  const filteredCoupons = useMemo(() => {
    const term = search.trim().toLowerCase();
    return coupons.filter((coupon) => {
      const matchesSearch =
        !term ||
        [coupon.code, coupon.title, coupon.description]
          .filter(Boolean).join(" ").toLowerCase().includes(term);
      if (!matchesSearch) return false;
      if (filter === "active") return coupon.active !== false;
      if (filter === "inactive") return coupon.active === false;
      if (filter === "public") return coupon.visible !== false;
      if (filter === "hidden") return coupon.visible === false;
      return true;
    });
  }, [coupons, filter, search]);

  const stats = useMemo(() => {
    const activeCount = coupons.filter((item) => item.active !== false).length;
    const publicCount = coupons.filter((item) => item.visible !== false).length;
    const redeemedCount = usage.length;
    const totalSavings = usage.reduce((sum, item) => sum + (Number(item.savingsAmount) || 0), 0);
    return { activeCount, publicCount, redeemedCount, totalSavings };
  }, [coupons, usage]);

  const usageByCoupon = useMemo(() => {
    return usage.reduce((acc, item) => {
      const key = item.couponCode || "UNKNOWN";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
  }, [usage]);

  const topUsageRows = useMemo(() => {
    return [...usage]
      .sort((a, b) => {
        const aTime = new Date(a.redeemedAt?.seconds ? a.redeemedAt.seconds * 1000 : a.redeemedAt || 0).getTime();
        const bTime = new Date(b.redeemedAt?.seconds ? b.redeemedAt.seconds * 1000 : b.redeemedAt || 0).getTime();
        return bTime - aTime;
      })
      .slice(0, 20);
  }, [usage]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId("");
    setShowForm(false);
  };

  const openCreate = () => {
    setForm(emptyForm);
    setEditingId("");
    setShowForm(true);
    setNotice({ tone: "", message: "" });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const code = normalizeCouponInput(form.code);

    if (!code || !form.title || !form.discountValue) {
      setNotice({ tone: "error", message: "Code, title, and discount value are required." });
      return;
    }

    // Validate percent range
    if (form.discountType === "percent" && (Number(form.discountValue) <= 0 || Number(form.discountValue) > 100)) {
      setNotice({ tone: "error", message: "Percentage must be between 1 and 100." });
      return;
    }

    try {
      setSaving(true);
      setNotice({ tone: "", message: "" });

      // Convert rupees → paise for flat values
      const discountValue =
        form.discountType === "flat"
          ? rupeesToPaise(form.discountValue)
          : Math.round(Number(form.discountValue) || 0);

      const payload = {
        code,
        title: form.title.trim(),
        description: form.description.trim(),
        discountType: form.discountType,
        discountValue,
        maxDiscountCap: rupeesToPaise(form.maxDiscountCap),
        usageLimit: Number(form.usageLimit || 0) || 0,
        perUserUsageLimit: Number(form.perUserUsageLimit || 0) || 0,
        minimumAmount: rupeesToPaise(form.minimumAmount),
        badge: form.badge.trim() || "Limited",
        visible: Boolean(form.visible),
        active: Boolean(form.active),
        allowedPlans: form.allowedPlans,
        expiresAt: form.expiresAt ? new Date(form.expiresAt) : null,
        updatedAt: serverTimestamp(),
      };

      if (editingId) {
        await updateDoc(doc(db, "coupons", editingId), payload);
        setNotice({ tone: "success", message: `${code} updated successfully.` });
      } else {
        await addDoc(collection(db, "coupons"), {
          ...payload,
          usageCount: 0,
          createdAt: serverTimestamp(),
        });
        setNotice({ tone: "success", message: `${code} created successfully.` });
      }
      resetForm();
      // refresh coupons list
      queryClient.invalidateQueries(["adminCouponsPageData"]);
    } catch (error) {
      setNotice({ tone: "error", message: error.message || "Could not save coupon." });
    } finally {
      setSaving(false);
    }
  };

  const editCoupon = (coupon) => {
    setEditingId(coupon.id);
    const isFlat = coupon.discountType === "flat";
    setForm({
      code: coupon.code || "",
      title: coupon.title || "",
      description: coupon.description || "",
      discountType: coupon.discountType || "percent",
      discountValue: isFlat
        ? String(paiseToRupees(coupon.discountValue) || "")
        : String(coupon.discountValue ?? ""),
      maxDiscountCap: coupon.maxDiscountCap ? String(paiseToRupees(coupon.maxDiscountCap)) : "",
      usageLimit: String(coupon.usageLimit ?? ""),
      perUserUsageLimit: String(coupon.perUserUsageLimit ?? "1"),
      minimumAmount: coupon.minimumAmount ? String(paiseToRupees(coupon.minimumAmount)) : "",
      badge: coupon.badge || "Limited",
      visible: coupon.visible !== false,
      active: coupon.active !== false,
      allowedPlans: Array.isArray(coupon.allowedPlans) ? coupon.allowedPlans : [],
      expiresAt: toDateTimeLocal(coupon.expiresAt),
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleCoupon = async (coupon, field) => {
    await updateDoc(doc(db, "coupons", coupon.id), {
      [field]: !(coupon[field] !== false),
      updatedAt: serverTimestamp(),
    });
    queryClient.invalidateQueries(["adminCouponsPageData"]);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await deleteDoc(doc(db, "coupons", deleteTarget.id));
    setNotice({ tone: "success", message: `${deleteTarget.code} deleted.` });
    setDeleteTarget(null);
    queryClient.invalidateQueries(["adminCouponsPageData"]);
  };

  const copyCode = async (code) => {
    await navigator.clipboard.writeText(code);
    setCopiedCode(code);
    window.setTimeout(() => setCopiedCode(""), 1400);
  };

  const exportUsageCsv = () => {
    const rows = usage.map((item) => ({
      couponCode: item.couponCode || "",
      couponTitle: item.couponTitle || "",
      uid: item.uid || "",
      profileId: item.profileId || "",
      paymentId: item.paymentId || "",
      orderId: item.orderId || "",
      planName: item.planName || "",
      discountAmount: Number(item.discountAmount || 0) / 100,
      savingsAmount: Number(item.savingsAmount || 0) / 100,
      finalAmount: Number(item.finalAmount || 0) / 100,
      redeemedAt: formatMembershipDate(parseMembershipDate(item.redeemedAt)),
    }));
    downloadCsv("coupon-usage.csv", toCsv(rows));
  };

  const setField = (field, value) => setForm((c) => ({ ...c, [field]: value }));

  return (
    <AdminLayout>
      {/* HEADER */}
      <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="section-subtle">Manage</p>
          <h1 className="mt-1 font-display text-3xl font-bold text-ink-800">Coupons</h1>
          <p className="mt-1 text-[13.5px] text-ink-400">
            Create offers, control discount rules, and track redemptions.
          </p>
        </div>
        <button type="button" onClick={openCreate} className="btn-primary px-5">
          <Plus size={16} /> New coupon
        </button>
      </div>

      {notice.message && (
        <div className={`mb-4 rounded-2xl px-4 py-3 text-[13px] font-semibold ${
          notice.tone === "success" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
        }`}>
          {notice.message}
        </div>
      )}

      {/* STATS */}
      <div className="mb-6 grid grid-cols-2 gap-3 xl:grid-cols-4">
        <StatCard label="Active coupons" value={stats.activeCount} Icon={TicketPercent} tint="bg-brand-50 text-brand-600" />
        <StatCard label="Public offers" value={stats.publicCount} Icon={Tag} tint="bg-sky-50 text-sky-600" />
        <StatCard label="Redemptions" value={stats.redeemedCount} Icon={Users} tint="bg-violet-50 text-violet-600" />
        <StatCard label="User savings" value={formatCurrency(stats.totalSavings)} Icon={IndianRupee} tint="bg-emerald-50 text-emerald-600" valueClass="text-emerald-600" />
      </div>

      {/* COUPON LIST */}
      <div className="card p-4 sm:p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="input-group flex-1">
            <Search size={16} className="text-ink-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search code, title, description..."
            />
            {search && (
              <button onClick={() => setSearch("")} className="text-ink-400 hover:text-brand-600">
                <X size={14} />
              </button>
            )}
          </div>
          <div className="-mx-1 flex flex-wrap gap-1.5">
            {["all", "active", "inactive", "public", "hidden"].map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setFilter(value)}
                className={`rounded-xl px-3 py-1.5 text-[12.5px] font-semibold capitalize transition ${
                  filter === value
                    ? "bg-brand-gradient text-white shadow-soft"
                    : "border border-sand-200 bg-white text-ink-600 hover:border-brand-600 hover:text-brand-600"
                }`}
              >
                {value}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5 grid gap-3 lg:grid-cols-2">
          {loading ? (
            <div className="col-span-full flex justify-center py-12">
              <Loader2 className="animate-spin text-brand-600" size={28} />
            </div>
          ) : filteredCoupons.length === 0 ? (
            <div className="col-span-full flex flex-col items-center rounded-2xl bg-sand-50 px-4 py-12 text-center">
              <TicketPercent className="text-ink-300" size={32} />
              <p className="mt-2 text-[13px] font-semibold text-ink-600">No coupons found</p>
              <button type="button" onClick={openCreate} className="btn-primary mt-4 px-5">
                <Plus size={14} /> Create your first coupon
              </button>
            </div>
          ) : (
            filteredCoupons.map((coupon) => (
              <article key={coupon.id} className="overflow-hidden rounded-3xl border border-sand-200 bg-white">
                {/* Ticket header */}
                <div className="relative flex items-center justify-between gap-3 bg-brand-gradient px-4 py-3 text-white">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/15">
                      <TicketPercent size={16} />
                    </div>
                    <div>
                      <p className="font-display text-[15px] font-bold leading-tight">{coupon.code}</p>
                      <p className="text-[11px] text-white/80">{formatDiscountLabel(coupon)}</p>
                    </div>
                  </div>
                  <div className="flex gap-1.5">
                    {coupon.active === false && (
                      <span className="rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-bold uppercase">Inactive</span>
                    )}
                    {coupon.visible === false && (
                      <span className="rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-bold uppercase">Hidden</span>
                    )}
                  </div>
                </div>

                {/* Body */}
                <div className="px-4 py-3">
                  <h3 className="font-semibold text-ink-800">{coupon.title || coupon.code}</h3>
                  <p className="mt-0.5 text-[12.5px] text-ink-400">
                    {coupon.description || "No description added."}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    <Chip>Redeemed {usageByCoupon[coupon.code] || 0}×</Chip>
                    {coupon.minimumAmount > 0 && <Chip>Min {formatCurrency(coupon.minimumAmount)}</Chip>}
                    {coupon.usageLimit > 0 && <Chip>Limit {coupon.usageLimit}</Chip>}
                    <Chip>Expires {formatMembershipDate(parseMembershipDate(coupon.expiresAt)) || "—"}</Chip>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-1.5 border-t border-dashed border-sand-200 bg-sand-50/40 px-4 py-2.5">
                  <ActionBtn onClick={() => copyCode(coupon.code)} Icon={copiedCode === coupon.code ? CheckCircle2 : Copy} label="Copy" />
                  <ActionBtn onClick={() => editCoupon(coupon)} Icon={Pencil} label="Edit" />
                  <ActionBtn
                    onClick={() => toggleCoupon(coupon, "active")}
                    Icon={coupon.active === false ? ToggleLeft : ToggleRight}
                    label={coupon.active === false ? "Enable" : "Disable"}
                  />
                  <ActionBtn
                    onClick={() => toggleCoupon(coupon, "visible")}
                    Icon={Tag}
                    label={coupon.visible === false ? "Show" : "Hide"}
                  />
                  <ActionBtn onClick={() => setDeleteTarget(coupon)} Icon={Trash2} label="Delete" danger />
                </div>
              </article>
            ))
          )}
          {/* sentinel & load more */}
          <div className="col-span-full flex justify-center py-4">
            <div ref={sentinelRef} />
            {couponsQuery.isFetchingNextPage ? (
              <Loader2 className="animate-spin text-brand-600" size={20} />
            ) : couponsQuery.hasNextPage ? (
              <button type="button" onClick={() => couponsQuery.fetchNextPage()} className="btn-ghost px-4 py-2">
                Load more
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {/* ANALYTICS */}
      <div className="card mt-6 p-4 sm:p-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-bold text-ink-800">Redemption analytics</h2>
            <p className="text-[12.5px] text-ink-400">Who redeemed offers — export anytime.</p>
          </div>
          <button type="button" onClick={exportUsageCsv} className="btn-outline px-4 py-2 text-[12.5px]">
            <Download size={14} /> Export CSV
          </button>
        </div>

        <div className="mt-4 overflow-hidden rounded-2xl border border-sand-200">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-[13px]">
              <thead className="bg-sand-50 text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                <tr>
                  <th className="px-4 py-3">Coupon</th>
                  <th className="px-4 py-3">Member</th>
                  <th className="px-4 py-3">Plan</th>
                  <th className="px-4 py-3">Saved</th>
                  <th className="px-4 py-3">Redeemed</th>
                </tr>
              </thead>
              <tbody>
                {topUsageRows.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-ink-400">No redemptions yet.</td></tr>
                ) : (
                  topUsageRows.map((item) => (
                    <tr key={item.id} className="border-t border-sand-100">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-ink-800">{item.couponCode || "-"}</div>
                        <div className="text-[11px] text-ink-400">{item.couponTitle || "-"}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-ink-800">{item.uid || "-"}</div>
                        <div className="text-[11px] text-ink-400">Profile {item.profileId || "-"}</div>
                      </td>
                      <td className="px-4 py-3 text-ink-700">{item.planName || "-"}</td>
                      <td className="px-4 py-3 font-semibold text-emerald-600">{formatCurrency(item.savingsAmount || 0)}</td>
                      <td className="px-4 py-3 text-ink-700">{formatMembershipDate(parseMembershipDate(item.redeemedAt))}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* CREATE / EDIT FORM MODAL */}
      {showForm && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-ink-900/50 px-4 backdrop-blur-sm sm:items-center" onClick={resetForm}>
          <form
            onSubmit={handleSubmit}
            onClick={(e) => e.stopPropagation()}
            className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-[28px] bg-white shadow-elevated sm:rounded-3xl"
          >
            <div className="flex items-center justify-between border-b border-sand-200 px-6 py-4">
              <div>
                <h2 className="font-display text-xl font-bold text-ink-800">
                  {editingId ? "Edit coupon" : "Create coupon"}
                </h2>
                <p className="text-[12.5px] text-ink-400">Configure discount rules and limits</p>
              </div>
              <button type="button" onClick={resetForm} className="btn-icon h-9 w-9">
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5">
              <div className="grid gap-4">
                {/* Code + title */}
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="field-label">Coupon code *</label>
                    <input
                      value={form.code}
                      onChange={(e) => setField("code", normalizeCouponInput(e.target.value))}
                      placeholder="E.g. SAVE500"
                      className="input uppercase tracking-wider"
                    />
                  </div>
                  <div>
                    <label className="field-label">Offer title *</label>
                    <input
                      value={form.title}
                      onChange={(e) => setField("title", e.target.value)}
                      placeholder="E.g. Festive Offer"
                      className="input"
                    />
                  </div>
                </div>

                <div>
                  <label className="field-label">Description</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setField("description", e.target.value)}
                    placeholder="Short description shown to users"
                    rows={2}
                    className="input resize-none"
                  />
                </div>

                {/* Discount type toggle */}
                <div>
                  <label className="field-label">Discount type *</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setField("discountType", "percent")}
                      className={`flex items-center justify-center gap-2 rounded-2xl border-2 py-3 text-[13px] font-semibold transition ${
                        form.discountType === "percent"
                          ? "border-brand-600 bg-brand-50 text-brand-700"
                          : "border-sand-200 bg-white text-ink-600"
                      }`}
                    >
                      <Percent size={16} /> Percentage
                    </button>
                    <button
                      type="button"
                      onClick={() => setField("discountType", "flat")}
                      className={`flex items-center justify-center gap-2 rounded-2xl border-2 py-3 text-[13px] font-semibold transition ${
                        form.discountType === "flat"
                          ? "border-brand-600 bg-brand-50 text-brand-700"
                          : "border-sand-200 bg-white text-ink-600"
                      }`}
                    >
                      <IndianRupee size={16} /> Flat amount
                    </button>
                  </div>
                </div>

                {/* Discount value */}
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="field-label">
                      {form.discountType === "percent" ? "Discount percentage *" : "Discount amount *"}
                    </label>
                    <div className="input-group">
                      {form.discountType === "percent"
                        ? <Percent size={15} className="text-ink-400" />
                        : <IndianRupee size={15} className="text-ink-400" />}
                      <input
                        type="number"
                        value={form.discountValue}
                        onChange={(e) => setField("discountValue", e.target.value)}
                        placeholder={form.discountType === "percent" ? "20" : "500"}
                        min="0"
                        max={form.discountType === "percent" ? "100" : undefined}
                      />
                      <span className="text-[13px] font-semibold text-ink-400">
                        {form.discountType === "percent" ? "%" : "₹"}
                      </span>
                    </div>
                    <p className="mt-1 text-[11px] text-ink-400">
                      {form.discountType === "percent"
                        ? "E.g. 20 means 20% off"
                        : "E.g. 500 means ₹500 off"}
                    </p>
                  </div>

                  {form.discountType === "percent" && (
                    <div>
                      <label className="field-label">Max discount cap (₹)</label>
                      <div className="input-group">
                        <IndianRupee size={15} className="text-ink-400" />
                        <input
                          type="number"
                          value={form.maxDiscountCap}
                          onChange={(e) => setField("maxDiscountCap", e.target.value)}
                          placeholder="Optional, e.g. 1000"
                          min="0"
                        />
                      </div>
                      <p className="mt-1 text-[11px] text-ink-400">Cap the max discount amount</p>
                    </div>
                  )}
                </div>

                {/* Limits */}
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="field-label">Minimum amount (₹)</label>
                    <div className="input-group">
                      <IndianRupee size={15} className="text-ink-400" />
                      <input
                        type="number"
                        value={form.minimumAmount}
                        onChange={(e) => setField("minimumAmount", e.target.value)}
                        placeholder="Optional"
                        min="0"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="field-label">Total usage limit</label>
                    <input
                      type="number"
                      value={form.usageLimit}
                      onChange={(e) => setField("usageLimit", e.target.value)}
                      placeholder="0 = unlimited"
                      className="input"
                      min="0"
                    />
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="field-label">Per-user limit</label>
                    <input
                      type="number"
                      value={form.perUserUsageLimit}
                      onChange={(e) => setField("perUserUsageLimit", e.target.value)}
                      placeholder="1"
                      className="input"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="field-label">Expires on</label>
                    <input
                      type="datetime-local"
                      value={form.expiresAt}
                      onChange={(e) => setField("expiresAt", e.target.value)}
                      className="input"
                    />
                  </div>
                </div>

                {/* Allowed plans */}
                <div>
                  <label className="field-label">Allowed plans <span className="font-normal text-ink-400">(none = all plans)</span></label>
                  <div className="flex flex-wrap gap-2">
                    {plans.map((plan) => {
                      const checked = form.allowedPlans.includes(plan.id);
                      return (
                        <button
                          key={plan.id}
                          type="button"
                          onClick={() =>
                            setField("allowedPlans", checked
                              ? form.allowedPlans.filter((p) => p !== plan.id)
                              : [...form.allowedPlans, plan.id])
                          }
                          className={`rounded-full px-3.5 py-2 text-[12.5px] font-semibold transition ${
                            checked
                              ? "bg-brand-gradient text-white shadow-soft"
                              : "border border-sand-200 bg-white text-ink-600 hover:border-brand-600"
                          }`}
                        >
                          {plan.name || plan.id}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Badge */}
                <div>
                  <label className="field-label">Badge text</label>
                  <input
                    value={form.badge}
                    onChange={(e) => setField("badge", e.target.value)}
                    placeholder="E.g. Limited, Festive, New"
                    className="input"
                  />
                </div>

                {/* Toggles */}
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="card flex cursor-pointer items-center justify-between p-3.5">
                    <div>
                      <p className="text-[13px] font-semibold text-ink-800">Public visible</p>
                      <p className="text-[11px] text-ink-400">Show on membership page</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={form.visible}
                      onChange={(e) => setField("visible", e.target.checked)}
                      className="h-5 w-5 accent-brand-600"
                    />
                  </label>
                  <label className="card flex cursor-pointer items-center justify-between p-3.5">
                    <div>
                      <p className="text-[13px] font-semibold text-ink-800">Active</p>
                      <p className="text-[11px] text-ink-400">Can be redeemed</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={form.active}
                      onChange={(e) => setField("active", e.target.checked)}
                      className="h-5 w-5 accent-brand-600"
                    />
                  </label>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="grid grid-cols-2 gap-3 border-t border-sand-200 px-6 py-4">
              <button type="button" onClick={resetForm} className="btn-ghost py-3">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary py-3">
                {saving ? <><Loader2 size={16} className="animate-spin" /> Saving...</> : editingId ? "Update coupon" : "Create coupon"}
              </button>
            </div>
          </form>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete coupon?"
        description={deleteTarget ? `"${deleteTarget.code}" will be permanently removed. This cannot be undone.` : ""}
        Icon={Trash2}
        tone="danger"
        confirmLabel="Delete"
        onConfirm={confirmDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </AdminLayout>
  );
}

/* ---------- Small components ---------- */
function StatCard({ label, value, Icon, tint, valueClass = "text-ink-800" }) {
  return (
    <div className="card flex items-center gap-3 p-4">
      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tint}`}>
        <Icon size={19} />
      </div>
      <div className="min-w-0">
        <p className={`font-display text-xl font-bold leading-none ${valueClass}`}>{value}</p>
        <p className="mt-1 text-[11px] font-semibold text-ink-400">{label}</p>
      </div>
    </div>
  );
}

function Chip({ children }) {
  return (
    <span className="rounded-full bg-sand-100 px-2.5 py-1 text-[11px] font-semibold text-ink-600">
      {children}
    </span>
  );
}

function ActionBtn({ onClick, Icon, label, danger }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-[12px] font-semibold transition ${
        danger
          ? "text-rose-600 hover:bg-rose-50"
          : "text-ink-600 hover:bg-white hover:text-brand-600"
      }`}
    >
      <Icon size={14} /> {label}
    </button>
  );
}
